import * as THREE from 'three'
import { mulberry32 } from '../prng.ts'

// FOUNDATION §3 — the glyph atlas: 8×8 = 64 cells. No PNG asset exists in the repo yet,
// so the atlas is generated at runtime, deterministically, on a canvas (ISSUES.md #1).
// ~36 real characters (heavy serif, ink bleed) + 28 invented glyphs drawn procedurally
// with the same stroke weight. White ink on transparent ground; tinted per-instance.

const REAL = [
  'A', 'E', 'H', 'K', 'M', 'R', 'S', 'T', 'X', 'Z', '2', '4', '7', '0', '9', '3',
  '{', '}', ';', '&', '%', '/', '\\', '<', '>', '[', ']',
  '¶', '†', '‡', '§', '№', '?', '¤', '±', '¬',
]

function drawInvented(ctx: CanvasRenderingContext2D, s: number, seed: number) {
  const r = mulberry32(seed * 7919 + 3)
  const px = (v: number) => (v - 0.5) * s
  ctx.lineWidth = s * 0.15
  ctx.lineCap = 'butt'
  // vertical spine
  const sx = px(0.3 + r() * 0.4)
  ctx.beginPath()
  ctx.moveTo(sx, px(0.08 + r() * 0.1))
  ctx.lineTo(sx, px(0.82 + r() * 0.1))
  ctx.stroke()
  // crossbars
  const n = 1 + Math.floor(r() * 3)
  for (let i = 0; i < n; i++) {
    const y = px(0.15 + r() * 0.7)
    ctx.beginPath()
    ctx.moveTo(px(0.12 + r() * 0.2), y)
    ctx.lineTo(px(0.68 + r() * 0.2), y + (r() < 0.35 ? px(0.18) * (r() - 0.5) : 0))
    ctx.stroke()
  }
  // hook
  if (r() < 0.6) {
    ctx.beginPath()
    ctx.arc(px(0.32 + r() * 0.36), px(0.3 + r() * 0.4), s * 0.15, r() * Math.PI, Math.PI * (1.1 + r()))
    ctx.stroke()
  }
  // terminal dot
  if (r() < 0.55) {
    ctx.beginPath()
    ctx.arc(px(0.15 + r() * 0.7), px(0.15 + r() * 0.7), s * 0.065, 0, Math.PI * 2)
    ctx.fill()
  }
}

export function makeGlyphAtlas(size = 2048): THREE.Texture {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')!
  const cell = size / 8
  ctx.fillStyle = '#ffffff'
  ctx.strokeStyle = '#ffffff'

  for (let i = 0; i < 64; i++) {
    const cx = (i % 8) * cell
    const cy = Math.floor(i / 8) * cell
    ctx.save()
    ctx.translate(cx + cell / 2, cy + cell / 2)
    ctx.globalAlpha = 1
    if (i < REAL.length) {
      ctx.font = `900 ${cell * 0.68}px Georgia, 'Iowan Old Style', 'Times New Roman', serif`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      // letterpress ink bleed: soft offset passes under the solid impression
      for (const [ox, oy, a] of [[3, 3, 0.45], [-3, 2, 0.45], [0, 0, 1], [0, 0, 1]] as const) {
        ctx.globalAlpha = a
        ctx.fillText(REAL[i], ox, oy)
      }
    } else {
      drawInvented(ctx, cell * 0.8, i)
    }
    ctx.restore()
  }

  const tex = new THREE.CanvasTexture(canvas)
  tex.anisotropy = 4
  tex.generateMipmaps = true
  tex.minFilter = THREE.LinearMipmapLinearFilter
  tex.magFilter = THREE.LinearFilter
  return tex
}
